import React from 'react';

// =============================================================
// Reusable expandable-row config for react-bootstrap-table-next.
//
// Presentation only. Builds the `expandRow` prop object from the SAME column
// definitions a table already uses — no data/redux/selector/handler changes.
// The expanded panel renders each column's `text` (label) + the row's
// formatted value (column.formatter if present, else the raw field) as a
// clean label→value list, brand-styled with cabinet tokens.
//
// Compact columns stay visible in the row; the rest are tagged `col-secondary`
// (see callers) and hidden by CSS under <1200px, but ALWAYS reappear here in
// the expanded panel so no information is lost.
// =============================================================

// Resolve a (possibly dotted) dataField path against a row, mirroring the way
// react-bootstrap-table-next reads cell values.
const getCellValue = (row, dataField) => {
  if (!dataField) return undefined;
  if (dataField.indexOf('.') === -1) return row[dataField];
  return dataField.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);
};

// Inline SVG chevron (no emoji glyphs per brand rule). Rotates via CSS class.
const Chevron = ({ expanded }) => (
  <span className={`mc-expand-chevron${expanded ? ' is-open' : ''}`} aria-hidden="true">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

// Render the label→value detail panel for one row, reusing column formatters.
const renderDetailPanel = (columns, row, rowIndex) => (
  <div className="mc-expand-panel">
    {columns
      .filter((col) => col && col.dataField && col.text)
      .map((col) => {
        const rawValue = getCellValue(row, col.dataField);
        // react-bootstrap-table formatter signature: (cell, row, rowIndex, formatExtraData)
        const value = col.formatter
          ? col.formatter(rawValue, row, rowIndex, col.formatExtraData)
          : rawValue;

        return (
          <div className="mc-expand-row" key={col.dataField}>
            <span className="mc-expand-label">{col.text}</span>
            <span className="mc-expand-value">{value}</span>
          </div>
        );
      })}
  </div>
);

/**
 * Build a react-bootstrap-table-next `expandRow` config from columns.
 *
 * @param {Array} columns – the SAME column definitions the table renders.
 * @param {Object} [options]
 * @param {boolean} [options.onlyOneExpanding=false]
 * @returns {Object} expandRow prop object
 */
export const buildExpandRow = (columns, options = {}) => ({
  renderer: (row, rowIndex) => renderDetailPanel(columns, row, rowIndex),
  showExpandColumn: true,
  expandByColumnOnly: false, // whole row is tappable
  onlyOneExpanding: options.onlyOneExpanding || false,
  className: 'mc-expanded-row',
  expandColumnPosition: 'right',
  expandHeaderColumnRenderer: () => <span className="mc-expand-th" aria-hidden="true" />,
  expandColumnRenderer: ({ expanded }) => <Chevron expanded={expanded} />,
  ...(options.onExpand ? { onExpand: options.onExpand } : {}),
});

export default buildExpandRow;
